import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Divider,
  Group,
  Indicator,
  Popover,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertTriangle, IconClockPause, IconExternalLink } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatRelativeTime } from '../lib/formatters';
import type { PendingWorkCounters, PendingWorkTicket } from '../lib/api/domains/portalTimeBooking';

/**
 * Kopfzeilen-Anzeige für eigene Schreibvorgänge, die noch nicht in weclapp sind.
 *
 * Bis dahin endete der Notbetrieb für den Techniker bei der gelben Meldung „Im Portal
 * gespeichert — Weclapp folgt". Ob es später wirklich ankam, erfuhr er nie: Der Nachsync
 * läuft still, und ein endgültig gescheiterter Vorgang (fünf Ablehnungen, danach fasst ihn
 * kein Job mehr an) war nirgends zu sehen. Wer eine Änderung für erledigt hält, die nie
 * angekommen ist, trägt sie kein zweites Mal ein.
 *
 * **Nichts anzeigen, wenn nichts wartet.** Im Regelbetrieb sind alle Wartelisten leer; ein
 * Dauerelement mit einer „0" würde die Kopfzeile verbrauchen und nach zwei Wochen niemandem
 * mehr auffallen, wenn dort eine echte Zahl steht.
 *
 * **Zwei Zahlen, zwei Farben.** Gelb heißt „läuft von allein" — die richtige Reaktion ist
 * Nichtstun, und eine Warnfarbe wäre falscher Alarm. Rot heißt „kein Job versucht das noch",
 * und dann muss jemand ran. Zusammengezählt hätte der häufige Fall den seltenen versteckt,
 * auf den es ankommt.
 */
export function PendingWeclappWorkIndicator() {
  const [opened, setOpened] = useState(false);
  const isMobile = useMediaQuery('(max-width: 48em)');
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['weclapp-pending-work'],
    queryFn: () => api.getWeclappPendingWork(),
    refetchInterval: 60_000,
  });

  if (!data || (data.waiting === 0 && data.stuck === 0)) return null;

  const hasStuck = data.stuck > 0;
  const color = hasStuck ? 'red' : 'yellow';
  const total = data.waiting + data.stuck;
  const label = hasStuck
    ? `${data.stuck} ${data.stuck === 1 ? 'Vorgang' : 'Vorgänge'} nicht nach Weclapp übertragen`
    : `${data.waiting} ${data.waiting === 1 ? 'Vorgang wartet' : 'Vorgänge warten'} auf Weclapp`;

  const openTicket = (t: PendingWorkTicket) => {
    setOpened(false);
    navigate(t.ticketNumber ? `/t/${t.ticketNumber}` : `/tickets`);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width="min(380px, 92vw)"
      shadow="lg"
    >
      <Popover.Target>
        <Tooltip label={label} multiline w={260} withArrow>
          {isMobile ? (
            <ActionIcon
              variant="light"
              color={color}
              aria-label={label}
              onClick={() => setOpened((o) => !o)}
            >
              <Indicator processing={hasStuck} color={color} size={6} disabled={!hasStuck}>
                {hasStuck ? <IconAlertTriangle size={16} /> : <IconClockPause size={16} />}
              </Indicator>
            </ActionIcon>
          ) : (
            <Badge
              color={color}
              variant="light"
              leftSection={
                hasStuck ? <IconAlertTriangle size={12} /> : <IconClockPause size={12} />
              }
              style={{ cursor: 'pointer' }}
              onClick={() => setOpened((o) => !o)}
            >
              {total} {hasStuck ? 'nicht übertragen' : 'wartet auf Weclapp'}
            </Badge>
          )}
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        <Box p="sm">
          <Text size="sm" fw={600} mb={4}>
            {hasStuck ? 'Nicht in Weclapp angekommen' : 'Wartet auf Weclapp'}
          </Text>
          <PendingWorkExplanation counters={data} />
        </Box>

        <Divider />

        <Box p="sm">
          <PendingWorkBreakdown counters={data} />
        </Box>

        {data.tickets.length > 0 && (
          <>
            <Divider />
            <Stack gap={0}>
              {data.tickets.map((t) => (
                <UnstyledButton key={t.ticketId} onClick={() => openTicket(t)} p="xs">
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      <ThemeIcon size="sm" variant="light" color={t.stuck > 0 ? 'red' : 'yellow'}>
                        {t.stuck > 0 ? (
                          <IconAlertTriangle size={13} />
                        ) : (
                          <IconClockPause size={13} />
                        )}
                      </ThemeIcon>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>
                          {t.ticketNumber ?? t.ticketId}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {t.subject ?? 'Betreff noch nicht gespiegelt'}
                        </Text>
                      </div>
                    </Group>
                    <Group gap={4} wrap="nowrap">
                      <Badge size="xs" variant="light" color={t.stuck > 0 ? 'red' : 'yellow'}>
                        {t.stuck > 0 ? t.stuck : t.waiting}
                      </Badge>
                      <IconExternalLink size={13} color="var(--mantine-color-dimmed)" />
                    </Group>
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}

function PendingWorkExplanation({ counters }: { counters: PendingWorkCounters }) {
  if (counters.stuck > 0) {
    return (
      <Text size="xs" c="dimmed">
        {counters.stuck === 1 ? 'Ein Vorgang' : `${counters.stuck} Vorgänge`} lässt sich nicht
        übertragen — Weclapp hat abgelehnt oder der Versuch brach unklar ab. Der Inhalt liegt
        im Portal, in Weclapp fehlt er. Bitte einem Admin melden; automatisch passiert hier
        nichts mehr.
        {counters.waiting > 0 &&
          ` Weitere ${counters.waiting} warten noch und gehen von allein raus.`}
      </Text>
    );
  }
  return (
    <Text size="xs" c="dimmed">
      Alles ist im Portal gespeichert und geht automatisch nach Weclapp, sobald es wieder
      antwortet. Du musst nichts erneut eintragen.
      {counters.oldestWaitingAt && ` Ältester Vorgang: ${formatRelativeTime(counters.oldestWaitingAt)}.`}
    </Text>
  );
}

const KIND_LABEL = {
  TICKET: 'Neue Tickets',
  EDIT: 'Ticket-Änderungen',
  COMMENT: 'Kommentare',
  DOCUMENT: 'Anhänge',
  TIME_BOOKING: 'Zeitbuchungen',
} as const;

function PendingWorkBreakdown({ counters }: { counters: PendingWorkCounters }) {
  const rows = (Object.keys(KIND_LABEL) as Array<keyof typeof KIND_LABEL>)
    .map((kind) => ({ kind, ...(counters.byKind[kind] ?? { waiting: 0, stuck: 0 }) }))
    .filter((r) => r.waiting > 0 || r.stuck > 0);

  return (
    <Stack gap={6}>
      {rows.map((r) => (
        <Group key={r.kind} justify="space-between" gap="xs">
          <Text size="xs">{KIND_LABEL[r.kind]}</Text>
          <Group gap={4}>
            {r.waiting > 0 && (
              <Badge size="xs" variant="light" color="yellow">
                {r.waiting} wartet
              </Badge>
            )}
            {r.stuck > 0 && (
              <Badge size="xs" variant="light" color="red">
                {r.stuck} hängt
              </Badge>
            )}
          </Group>
        </Group>
      ))}
    </Stack>
  );
}
